import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { looksLikeOpaqueUserId } from "@sd/lib/ticketRowMapping";
import { getUserDisplayFromMap } from "@sd/lib/userDisplayMap";
import { parseTicketNotesResponse } from "@sd/lib/ticketNotesApi";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import ServiceDeskAPIService, {
  serviceDeskTicketNotesQueryKey,
} from "@sd/services/APIs";

export function useTicketWorkbenchComments(ticketId: string | null) {
  const { userDisplayMap, agentEmployees } = useServiceDeskUserDisplayMap({
    refetchAgentListsOnMount: false,
  });

  const {
    data: notesRaw,
    isPending: notesPending,
    isError: notesError,
  } = useQuery({
    queryKey: serviceDeskTicketNotesQueryKey(ticketId),
    queryFn: async () => {
      const res = await ServiceDeskAPIService.ticketNotes.list(
        { ticket_id: ticketId!, page: 1, page_size: 200 },
        { skipErrorNotification: true }
      );
      return res.data;
    },
    enabled: Boolean(ticketId),
    staleTime: 15_000,
    retry: 1,
  });

  const notes = useMemo(
    () => parseTicketNotesResponse(notesRaw),
    [notesRaw]
  );

  const resolveCommentAuthor = useMemo(() => {
    return (raw: string | undefined | null) => {
      if (!raw) return "—";
      const t = String(raw).trim();
      if (!t) return "—";
      if (looksLikeOpaqueUserId(t)) {
        return (
          getUserDisplayFromMap(userDisplayMap, t) ??
          agentEmployees.find(a => a.id === t)?.name ??
          t
        );
      }
      return t;
    };
  }, [userDisplayMap, agentEmployees]);

  return {
    notes,
    notesPending: Boolean(ticketId) && notesPending,
    notesError: notesError && !notesRaw,
    resolveCommentAuthor,
  };
}
